import { getCsrfTokenAction } from "@/actions/serverActions/auth/getCsrfTokenAction";
import { COOKIES_NAME_CONFIG } from "@/config";

/**
 * Browser-side CSRF helper with three-layer caching.
 *
 * Layer 1 (in-memory):    set on first call, instant on subsequent reads
 * Layer 2 (cookie):       survives reloads, ~1h TTL set by `getCsrfTokenAction`
 * Layer 3 (server action): mints a fresh token only when neither above exists
 *
 * Concurrent first-time callers share a single in-flight RPC via the
 * `inflightFetch` promise — protects against thundering herd when multiple
 * mutations fire simultaneously on a fresh page load.
 */

let cachedToken: string | null = null;
let inflightFetch: Promise<string> | null = null;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  return match.split("=").slice(1).join("=");
}

/**
 * Returns a CSRF token, minting one server-side if needed. Always resolves
 * synchronously after the first successful call until `clearCsrfTokenCache()`
 * is called (e.g., after logout).
 */
export async function ensureCsrfToken(): Promise<string> {
  // Layer 1: in-memory hit
  if (cachedToken) return cachedToken;

  // Layer 2: cookie still valid (e.g., after page reload)
  const fromCookie = readCookie(COOKIES_NAME_CONFIG.XSRF_TOKEN);
  if (fromCookie) {
    cachedToken = fromCookie;
    return fromCookie;
  }

  // Layer 3: dedupe concurrent first-time calls
  if (inflightFetch) return inflightFetch;

  inflightFetch = getCsrfTokenAction()
    .then((fresh) => {
      cachedToken = fresh;
      return fresh;
    })
    .finally(() => {
      inflightFetch = null;
    });

  return inflightFetch;
}

/**
 * Clear the in-memory token cache.
 *
 * Call this after logout (or when you know the cookie has been cleared) so
 * the next mutation re-mints a fresh token.
 */
export function clearCsrfTokenCache(): void {
  cachedToken = null;
  inflightFetch = null;
}
